import xior, {type XiorError, type XiorRequestConfig} from 'xior';
import jsGlobalConfig from '../../../globals/jsGlobalConfig';

type XiorClient = ReturnType<typeof xior.create>;

/**
 * Adds a response interceptor to the given client:
 * - logs 408 timeouts
 * - on 403 (expired CSRF token) fetches a new token and retries the request (max. 3 times)
 */
export default function addCsrfRetryInterceptor(client: XiorClient) {
  async function getNewToken(): Promise<string> {
    const response = await client.get('/token/', {headers: {Accept: '*/*'}});
    return response.data;
  }

  client.interceptors.response.use(
    (response) => response,
    async (error: XiorError) => {
      const statusCode = error?.response?.status;

      if (statusCode === 408) {
        window.logJSError('timeout', null, null, null, error);
      }
      else if (statusCode === 403) {
        const config = error.config as XiorRequestConfig & { retryCount?: number };
        if (!config.retryCount || config.retryCount <= 2) {
          window.__globalConfig.csrfToken.value = await getNewToken();

          // There are 2 methods for passing the token. Header has priority, so we update only this.
          Object.assign(
            config.headers,
            jsGlobalConfig().getCsrfTokenHeader(),
            {retryCount: config.retryCount ? config.retryCount + 1 : 1}
          );

          // retry the request with new token
          return client.request(config);
        }
      }

      return Promise.reject(error);
    }
  );
}
